// src/components/ui/Button.tsx
import { motion } from 'framer-motion'
import { type ButtonHTMLAttributes, type ReactNode } from 'react'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost'
  size?: 'sm' | 'md' | 'lg'
  children: ReactNode
  loading?: boolean
}

const variants = {
  primary: 'bg-coral text-white shadow-lg hover:bg-coral/90',
  secondary: 'bg-turquoise text-white shadow-lg hover:bg-turquoise/90',
  ghost: 'bg-transparent text-ink hover:bg-ink/5',
}

const sizes = {
  sm: 'px-4 py-2 text-base min-h-[40px]',
  md: 'px-6 py-3 text-lg min-h-[48px]',
  lg: 'px-8 py-4 text-xl min-h-[60px]',
}

export function Button({
  variant = 'primary',
  size = 'md',
  children,
  loading,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      whileHover={{ scale: 1.02 }}
      className={`
        rounded-2xl font-heading font-bold
        transition-colors duration-150
        disabled:opacity-50 disabled:cursor-not-allowed
        ${variants[variant]}
        ${sizes[size]}
      `}
      disabled={disabled || loading}
      {...(props as any)}
    >
      {loading ? (
        <motion.span
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
          className="inline-block"
        >
          ●
        </motion.span>
      ) : (
        children
      )}
    </motion.button>
  )
}
